import React, { useMemo } from 'react'
import { MONTH_NAMES, splitMonth, joinMonth } from './overview'

// ─────────────────────────────────────────────────────────────────────────────
//   Selector de mes, en inglés
//
//   Sustituye a `<input type="month">`. El control nativo rinde su etiqueta en
//   el locale del NAVEGADOR, que no tiene por qué ser el de la interfaz: un
//   Chrome en español mostraba "septiembre de 2026" en mitad de una pantalla en
//   inglés, y no hay atributo que lo cambie — `lang` en el input no afecta al
//   formateo del valor en Chrome, y el resto del formato (si el mes va antes o
//   después del año) tampoco es nuestro.
//
//   Dos <select> propios lo resuelven sin negociar con el navegador, y de paso
//   dejan el mes elegible con un clic en vez de dos.
// ─────────────────────────────────────────────────────────────────────────────

// Cuántos años atrás se ofrecen. Es un rango de UI, no un dato: no hay
// endpoint que diga desde cuándo hay libros, y pedir uno para poblar un
// desplegable sería una llamada de red al servicio de un <select>. El año que
// venga en `value` se añade siempre aunque caiga fuera, así que un mes guardado
// o enlazado desde otro sitio se sigue viendo en vez de desaparecer.
const YEARS_BACK = 3

// `labelPrefix` existe porque la pantalla monta DOS de estos a la vez, desde y
// hasta. Dos controles rotulados los dos "Month" no son etiquetas: no se pueden
// asociar a nada ni con la vista ni con un lector de pantalla, y un
// `getByLabelText('Month')` deja de identificar uno solo. Sin prefijo el rótulo
// es el de siempre, así que un uso de un único selector no cambia.
export default function MonthYearPicker({ value, onChange, idPrefix = 'cf-month', labelPrefix = '' }) {
  const { year, month } = splitMonth(value)
  const monthText = labelPrefix ? `${labelPrefix} month` : 'Month'
  const yearText = labelPrefix ? `${labelPrefix} year` : 'Year'

  const years = useMemo(() => {
    const thisYear = new Date().getFullYear()
    const window = []
    for (let y = thisYear - YEARS_BACK; y <= thisYear; y += 1) window.push(y)
    // Un mes fuera de la ventana sigue siendo seleccionable mientras lo esté.
    // Sin esto, abrir marzo de 2019 dejaría el <select> en blanco y el
    // siguiente cambio de mes saltaría a un año que el usuario no eligió.
    if (year && !window.includes(year)) window.push(year)
    return window.sort((a, b) => b - a)
  }, [year])

  // El mes y el año se cambian por separado, pero el valor es uno solo. Si
  // alguno todavía no es válido no se emite nada: es mejor que el control se
  // quede quieto a que reescriba el mes que el usuario está mirando por un
  // default que no pidió.
  const emit = (nextYear, nextMonth) => {
    if (!nextYear || !nextMonth) return
    onChange(joinMonth(nextYear, nextMonth))
  }

  return (
    <div className="cf-monthpicker" style={{ display: 'flex', gap: 8 }}>
      <div>
        <label className="form-label" htmlFor={`${idPrefix}-month`}>{monthText}</label>
        <select
          id={`${idPrefix}-month`}
          className="select cf-input"
          value={month ?? ''}
          onChange={(e) => emit(year, Number(e.target.value))}
          style={{ minWidth: 140 }}
        >
          {!month && <option value="">Month…</option>}
          {MONTH_NAMES.map((name, index) => (
            <option key={name} value={index + 1}>{name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-label" htmlFor={`${idPrefix}-year`}>{yearText}</label>
        <select
          id={`${idPrefix}-year`}
          className="select cf-input"
          value={year ?? ''}
          onChange={(e) => emit(Number(e.target.value), month)}
          style={{ minWidth: 100 }}
        >
          {!year && <option value="">Year…</option>}
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  )
}
